import { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { formatAddress } from '../lib/blockchain';

const getWalletAddress = (props) =>
  props?.account ||
  props?.wallet?.account ||
  props?.wallet?.address ||
  props?.walletState?.account ||
  props?.walletState?.address ||
  props?.metaMask?.account ||
  props?.metaMask?.address ||
  '';

const formatValue = (value, fallback = '—') => {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'number') return value.toLocaleString();
  return `${value}`;
};

const extractBalance = (payload) => {
  if (payload === undefined || payload === null) return '0';

  const candidates = [
    payload.balance,
    payload.formatted,
    payload.available,
    payload.amount,
    payload.data?.balance,
  ];

  const match = candidates.find((value) => value !== undefined && value !== null);
  return match ?? '0';
};

const extractTransactions = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.transactions)) return payload.transactions;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const transactionMatchesAddress = (transaction, address) => {
  if (!address) return true;
  return JSON.stringify(transaction || {}).toLowerCase().includes(address.toLowerCase());
};

const profileDisplayName = (profile) =>
  profile?.pseudo || profile?.name || profile?.username || profile?.display_name || 'No SSO profile';

const ITANI_PAY_API = (import.meta.env.VITE_ITANI_PAY_API_URL || 'https://pay.itaninetworkchain.com').replace(/\/+$/, '');
const SSO_TOKEN_KEYS = ['itani_sso_token', 'metani_sso_token', 'token'];
const MINI_KOBS_ASSET_ORDER = ['ITANI', 'AR', 'YUM', 'BES', 'HOS'];

const readSsoToken = () => {
  for (const key of SSO_TOKEN_KEYS) {
    const value = localStorage.getItem(key);
    if (value) return value;
  }
  return '';
};

const formatAssetBalance = (value, decimals = 3) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '0';
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: decimals }).format(n);
};

const formatFiatQuote = (quote) => {
  if (!quote || typeof quote !== 'object') return 'Prix reel indisponible';
  const amount = Number(quote.amount ?? quote.value ?? 0);
  const currency = quote.currency || 'EUR';
  if (!Number.isFinite(amount)) return 'Prix reel indisponible';
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount);
};

const sortMiniKobsAssets = (assets = []) => [...assets].sort((a, b) => {
  const ac = String(a.currency || a.code || '').toUpperCase();
  const bc = String(b.currency || b.code || '').toUpperCase();
  const ai = MINI_KOBS_ASSET_ORDER.indexOf(ac);
  const bi = MINI_KOBS_ASSET_ORDER.indexOf(bc);
  return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
});

export default function Wallet(props) {
  const account = getWalletAddress(props);
  const connectWallet =
    props?.connectWallet ||
    props?.onConnectWallet ||
    props?.wallet?.connectWallet ||
    props?.wallet?.connect ||
    props?.walletState?.connectWallet ||
    props?.walletState?.connect ||
    props?.metaMask?.connectWallet ||
    props?.metaMask?.connect;
  const isConnecting =
    props?.isConnecting ??
    props?.wallet?.isConnecting ??
    props?.wallet?.loading ??
    props?.walletState?.isConnecting ??
    props?.walletState?.loading ??
    props?.metaMask?.isConnecting ??
    props?.metaMask?.loading ??
    false;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [balance, setBalance] = useState('0');
  const [profile, setProfile] = useState(null);
  const [network, setNetwork] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [miniKobsWallet, setMiniKobsWallet] = useState(null);
  const [miniKobsError, setMiniKobsError] = useState('');

  useEffect(() => {
    const loadMiniKobs = async () => {
      const token = readSsoToken();
      if (!token) {
        setMiniKobsWallet(null);
        setMiniKobsError('Connexion Metani requise pour afficher le Mini Kobs commun.');
        return;
      }
      try {
        const response = await fetch(
          `${ITANI_PAY_API}/api/metani/wallet/assets?app=hudlife&preferred_currency=EUR`,
          { headers: { Accept: 'application/json', Authorization: `Bearer ${token}` }, credentials: 'include' }
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || `HTTP ${response.status}`);
        setMiniKobsWallet(data);
        setMiniKobsError('');
      } catch (err) {
        setMiniKobsWallet(null);
        setMiniKobsError(err?.message || 'Mini Kobs indisponible.');
      }
    };

    loadMiniKobs();
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');

      try {
        const requests = [
          api.chain.connectivity(),
          api.chain.transactions(25, 1),
          account ? api.wallet.balance(account) : Promise.resolve(null),
          account ? api.sso.profile(account) : Promise.resolve(null),
        ];

        const [connectivityResponse, txResponse, balanceResponse, profileResponse] =
          await Promise.allSettled(requests);

        if (connectivityResponse.status === 'fulfilled') {
          setNetwork(connectivityResponse.value);
        }

        if (txResponse.status === 'fulfilled') {
          const filteredTransactions = extractTransactions(txResponse.value)
            .filter((transaction) => transactionMatchesAddress(transaction, account))
            .slice(0, 10);

          setTransactions(filteredTransactions);
        } else {
          setTransactions([]);
        }

        if (balanceResponse.status === 'fulfilled' && balanceResponse.value) {
          setBalance(extractBalance(balanceResponse.value));
        } else {
          setBalance('0');
        }

        if (profileResponse.status === 'fulfilled' && profileResponse.value) {
          setProfile(profileResponse.value);
        } else {
          setProfile(null);
        }
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Unable to load wallet data.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [account]);

  const networkStatus = useMemo(() => {
    if (!network) return 'Unknown';
    return (
      network.status ||
      network.connectivity ||
      network.state ||
      (network.connected ? 'Connected' : 'Checking')
    );
  }, [network]);

  return (
    <div className="space-y-6">
      <section className="glass p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm uppercase tracking-wide text-slate-400">Wallet</p>
            <h1 className="mt-2 text-3xl font-semibold text-white">Connected account overview</h1>
            <p className="mt-2 text-slate-300">
              Review on-chain balance, SSO profile status and recent network activity.
            </p>
          </div>

          {!account ? (
            <button
              type="button"
              disabled={isConnecting}
              className="rounded-full bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => connectWallet?.()}
            >
              {isConnecting ? 'Connecting…' : 'Connect wallet'}
            </button>
          ) : (
            <div className="rounded-2xl bg-white/5 px-4 py-3 text-right">
              <p className="text-xs uppercase tracking-wide text-slate-400">Address</p>
              <p className="font-medium text-white">{formatAddress(account)}</p>
            </div>
          )}
        </div>
      </section>

      {!account ? (
        <section className="glass p-6">
          <h2 className="text-xl font-semibold text-white">No wallet connected</h2>
          <p className="mt-2 text-slate-300">
            Connect MetaMask to load your ITANI balance, SSO profile and transaction history.
          </p>
        </section>
      ) : (
        <>
          {error ? (
            <section className="glass border border-rose-500/30 p-4 text-rose-200">
              {error}
            </section>
          ) : null}

          <section className="glass p-6">
            <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
              <div>
                <p className="text-sm uppercase tracking-wide text-cyan-200">Mini Kobs Wallet</p>
                <h2 className="mt-2 text-2xl font-semibold text-white">Portefeuille Metani commun</h2>
                <p className="mt-2 text-slate-300">
                  Les soldes iTani Pay + blockchain sont lus depuis la source Kobs. Les prix fiat ne s’affichent que si un marche reel, une liquidite verifiee ou un oracle est branche.
                </p>
              </div>
              <a className="rounded-full border border-cyan-300/40 px-4 py-2 text-sm text-cyan-100 hover:bg-cyan-300/10" href="https://app.itaninetworkchain.com" target="_blank" rel="noreferrer">
                Ouvrir Kobs complet
              </a>
            </div>
            {miniKobsError ? <p className="mt-4 text-sm text-amber-200">{miniKobsError}</p> : null}
            {miniKobsWallet?.assets?.length ? (
              <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
                {sortMiniKobsAssets(miniKobsWallet.assets).map((asset) => (
                  <article key={asset.currency || asset.code} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                    <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-slate-400">
                      <span>{asset.code || asset.currency}</span>
                      <strong className="text-cyan-100">{asset.symbol}</strong>
                    </div>
                    <p className="mt-3 text-2xl font-semibold text-white">{formatAssetBalance(asset.total_balance, asset.display_decimals)} {asset.symbol}</p>
                    <p className="mt-1 text-sm text-slate-400">{asset.name || asset.currency}</p>
                    <div className="mt-3 space-y-1 text-xs text-slate-300">
                      <p>Prix pays: {formatFiatQuote(asset.fiat_local)}</p>
                      <p>Prix reel: {formatFiatQuote(asset.fiat_preferred)}</p>
                      <p className="text-slate-500">Ledger {formatAssetBalance(asset.wallet_balance, asset.display_decimals)} · App {formatAssetBalance(asset.app_balance, asset.display_decimals)} · Chain {formatAssetBalance(asset.onchain_balance, asset.display_decimals)}</p>
                    </div>
                  </article>
                ))}
              </div>
            ) : null}
          </section>

          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <article className="glass p-5">
              <p className="text-sm text-slate-400">ITANI balance</p>
              <h2 className="mt-2 text-2xl font-semibold text-white">
                {loading ? 'Loading…' : formatValue(balance)}
              </h2>
            </article>

            <article className="glass p-5">
              <p className="text-sm text-slate-400">SSO profile</p>
              <h2 className="mt-2 text-2xl font-semibold text-white">
                {loading ? 'Loading…' : profileDisplayName(profile)}
              </h2>
              <p className="mt-1 text-sm text-slate-400">
                {profile?.bio || profile?.title || 'Profile lookup via Metani ID'}
              </p>
            </article>

            <article className="glass p-5">
              <p className="text-sm text-slate-400">Network</p>
              <h2 className="mt-2 text-2xl font-semibold text-white">{networkStatus}</h2>
              <p className="mt-1 text-sm text-slate-400">
                {formatValue(network?.message || network?.detail || network?.provider, 'Connectivity check')}
              </p>
            </article>

            <article className="glass p-5">
              <p className="text-sm text-slate-400">Recent matches</p>
              <h2 className="mt-2 text-2xl font-semibold text-white">{transactions.length}</h2>
              <p className="mt-1 text-sm text-slate-400">
                Transactions mentioning this wallet in the latest page.
              </p>
            </article>
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
            <article className="glass p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white">Recent transactions</h2>
                <span className="text-sm text-slate-400">Latest 10 filtered results</span>
              </div>

              {loading ? (
                <p className="mt-4 text-slate-300">Loading transaction history…</p>
              ) : transactions.length ? (
                <div className="mt-4 overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="text-slate-400">
                      <tr>
                        <th className="pb-3 pr-4 font-medium">Hash / ID</th>
                        <th className="pb-3 pr-4 font-medium">Type</th>
                        <th className="pb-3 pr-4 font-medium">Amount</th>
                        <th className="pb-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((transaction, index) => (
                        <tr key={transaction?.hash || transaction?.id || index} className="border-t border-white/5">
                          <td className="py-3 pr-4 text-white">
                            {formatValue(
                              transaction?.hash ||
                                transaction?.txid ||
                                transaction?.id ||
                                transaction?.transaction_id,
                              'Pending'
                            )}
                          </td>
                          <td className="py-3 pr-4 text-slate-300">
                            {formatValue(transaction?.type || transaction?.kind || transaction?.direction)}
                          </td>
                          <td className="py-3 pr-4 text-slate-300">
                            {formatValue(
                              transaction?.amount ||
                                transaction?.value ||
                                transaction?.amount_in ||
                                transaction?.amount_out,
                              '—'
                            )}
                          </td>
                          <td className="py-3 text-slate-300">
                            {formatValue(transaction?.status || transaction?.state || 'Confirmed')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="mt-4 text-slate-300">
                  No recent transactions were found for this address on the latest results page.
                </p>
              )}
            </article>

            <article className="glass p-6">
              <h2 className="text-xl font-semibold text-white">Network card</h2>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
                  <span className="text-slate-400">Connectivity</span>
                  <span className="text-white">{networkStatus}</span>
                </div>
                <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
                  <span className="text-slate-400">Wallet address</span>
                  <span className="text-white">{formatAddress(account)}</span>
                </div>
                <div className="flex items-center justify-between gap-4 border-b border-white/5 pb-3">
                  <span className="text-slate-400">SSO pseudo</span>
                  <span className="text-white">{profileDisplayName(profile)}</span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-slate-400">Profile bio</span>
                  <span className="max-w-[220px] text-right text-white">
                    {formatValue(profile?.bio || profile?.description, 'Not provided')}
                  </span>
                </div>
              </div>
            </article>
          </section>
        </>
      )}
    </div>
  );
}
